import { Popover, Transition } from "@headlessui/react";
import { routes, t } from "@prmf/site-router";
import { Form, Link, NavLink, useMatch, useSubmit } from "@remix-run/react";
import { Fragment } from "react";
import { twJoin, twMerge } from "tailwind-merge";
import BgImage from "./BgImage";
import { useProfile } from "./ProfileProvider";
import NotificationsButton from "./NotificationsButton";

const paths = {
  [routes.privateDevelopers]: "private",
  [routes.socialHousing]: "social",
  [routes.bespokeSolutions]: "bespoke",
  [routes.retailCustomers]: "retail",
};

export default function AuthBar() {
  const profile = useProfile();
  const submit = useSubmit();
  const isAccountPage = useMatch("/account/*") !== null;
  return (
    <div className="relative bg-[#292929] pb-2 text-xs text-white xl:pb-4">
      <div className="container relative flex justify-between gap-6 max-xl:hidden">
        <Popover>
          {profile.name ? (
            <div className="py-2">Hello, {profile.name}</div>
          ) : (
            <Popover.Button className="py-2 outline-none">
              Hello, customer (
              {t(profile.sector || "", "title", false) || "Unknown"})
            </Popover.Button>
          )}
          <Transition
            as={Fragment}
            enter="transition ease-in"
            leave="transition ease-ou"
            enterFrom="opacity-0"
            leaveTo="opacity-0"
            {...(profile.sector ? {} : { show: !isAccountPage })}
          >
            <Popover.Panel
              {...(profile.sector ? {} : { static: true })}
              className="fixed inset-0 z-50 grid place-items-center"
            >
              <Popover.Overlay
                className={twMerge(
                  "absolute inset-0",
                  profile.sector ? "bg-black/40" : "bg-black/70",
                )}
              />
              <Transition.Child
                as={Fragment}
                enter="transition ease-in"
                leave="transition ease-ou"
                enterFrom="-translate-y-10"
                leaveTo="translate-y-10"
              >
                <div className="relative m-4 max-w-3xl rounded bg-white p-4 text-black">
                  <h3 className="text-center text-lg font-semibold">
                    Choose your interest
                  </h3>
                  <h4 className="text-center text-lg font-light">
                    You can always adjust this later
                  </h4>
                  <Form
                    className="grid items-start gap-6 py-8 md:grid-cols-4 md:gap-8"
                    preventScrollReset
                  >
                    {[
                      routes.privateDevelopers,
                      routes.socialHousing,
                      routes.bespokeSolutions,
                      routes.retailCustomers,
                    ].map((r) => (
                      <Popover.Button
                        as="label"
                        className="cursor-pointer text-left"
                        key={r}
                      >
                        <BgImage
                          src={t(r, "image")}
                          className="relative hidden aspect-square overflow-hidden rounded md:block"
                        />
                        <div className="my-2 md:max-w-[200px]">
                          <h4 className="text-xs font-bold">{t(r, "title")}</h4>
                          <p className="text-xs">{t(r, "description")}</p>
                        </div>
                        <input
                          type="radio"
                          name="type"
                          value={paths[r]}
                          className="sr-only"
                          onClick={(e) =>
                            submit(e.currentTarget.form, {
                              action: location.pathname,
                            })
                          }
                        />
                      </Popover.Button>
                    ))}
                  </Form>
                </div>
              </Transition.Child>
            </Popover.Panel>
          </Transition>
        </Popover>
        <div className="">
          <div className="flex items-center justify-end gap-4">
            {profile.name ? (
              <Link to={routes.accountHome}>
                {t(routes.accountHome, "title")}
              </Link>
            ) : (
              <Link to={routes.accountLogin}>
                {t(routes.accountLogin, "title")}
              </Link>
            )}
            <NavLink to={routes.contact} className="py-2 [&.active]:font-bold">
              Contact us
            </NavLink>
            <NotificationsButton />
          </div>
          <div aria-hidden="true" className="mb-4 mt-2 h-9 min-w-[320px]" />
        </div>
      </div>
      <NavLink
        to={routes.home}
        className={twJoin(
          "container xl:block",
          "w-full max-w-[400px]",
          "py-1 xl:py-2",
          "ml-0 xl:mx-auto",
          "inset-0 xl:absolute",
        )}
      >
        <BgImage
          className="h-[70px] bg-transparent bg-contain bg-center xl:h-[85px]"
          src="/media/logo.svg"
        />
      </NavLink>
    </div>
  );
}
